import type { PipelineStepId } from '../model/brief-generation.types';
import {
  BRIEF_GEN_ANALYZE_CONTEXT_AGENT_ID,
  BRIEF_GEN_COMPOSE_AGENT_ID,
  BRIEF_GEN_EXTRACT_FILES_AGENT_ID,
} from '../model/brief-generation.constants';

/**
 * BE-emitted step label → stable FE step id.
 *
 * ONLY the 4 BE-driven steps are matchable. The 5th step (`saving-brief`)
 * is FE-only — its state is driven by the `useBriefGeneration` status
 * machine, never by a stream event — so it is intentionally NOT in this
 * map.
 *
 * The keys here MUST match the strings emitted by
 * `apps/api/src/agents/use-cases/brief-generation/brief-generation.agent.ts`
 * via `tracker.trackStep(<label>, ...)`. They are case-sensitive.
 */
const BE_STEP_LABELS = {
  'Reading files': 'reading-files',
  'Analyzing inputs': 'analyzing-inputs',
  'Checking knowledge base': 'checking-knowledge-base',
  'Composing brief': 'composing-brief',
} as const satisfies Record<string, Exclude<PipelineStepId, 'saving-brief'>>;

/**
 * Pure mapper from a BE step label to the FE step id.
 *
 * Returns `null` for unknown labels so callers can degrade gracefully —
 * they should `logger.debug` the unknown label and ignore it rather than
 * crashing or mutating any known step.
 *
 * The `Object.hasOwn` guard is used (instead of `as keyof typeof` + `??`)
 * because `noUncheckedIndexedAccess` is OFF in this workspace — so an `as`
 * cast with a `??` fallback gets flagged as an unnecessary conditional. We
 * narrow at runtime instead and let the dictionary lookup return safely.
 */
export const matchPipelineStep = (label: string): PipelineStepId | null => {
  if (Object.hasOwn(BE_STEP_LABELS, label)) {
    return BE_STEP_LABELS[label as keyof typeof BE_STEP_LABELS];
  }
  return null;
};

/**
 * Agent id → the steps that agent's node drives.
 *
 * An id rather than a label: the label matcher above compares a localized string the backend
 * chose, so a copy edit there silently empties the progress modal. An agent id is part of the
 * stream contract and changing it is a visible change.
 *
 * Several ids map to no step at all, and that is not an oversight. The workflow reports its own
 * root node and its summary step, and the modal has no row for either.
 */
const AGENT_STEPS: Record<string, readonly Exclude<PipelineStepId, 'saving-brief'>[]> = {
  [BRIEF_GEN_EXTRACT_FILES_AGENT_ID]: ['reading-files'],
  // Two steps for one agent. The knowledge-base lookup is a non-model call with no agent of its
  // own, made alongside the analysis, and the tracker this replaces opened both steps together.
  [BRIEF_GEN_ANALYZE_CONTEXT_AGENT_ID]: ['analyzing-inputs', 'checking-knowledge-base'],
  [BRIEF_GEN_COMPOSE_AGENT_ID]: ['composing-brief'],
};

export const matchPipelineSteps = (agentId: string): PipelineStepId[] =>
  Object.hasOwn(AGENT_STEPS, agentId) ? [...AGENT_STEPS[agentId]] : [];
